import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Alert } from '../common/Alert';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/datetime';
import { ImportPreview } from './ImportPreview';
import { ImportResult } from '../../types';

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const days = (n: number) => (n === 0 ? 'forever' : `${n} day${n === 1 ? '' : 's'}`);

/** Backups, retention and config-as-code — the operator's view of the data. */
export function BackupsCard() {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({ queryKey: ['backups'], queryFn: api.getBackupStatus });
  const run = useMutation({
    mutationFn: api.runBackup,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['backups'] });
      addToast('success', `Backup written: ${r.file.name} (${bytes(r.file.size_bytes)})${r.uploaded_to ? ', uploaded' : ''}`);
    },
    onError: err => { qc.invalidateQueries({ queryKey: ['backups'] }); addToast('error', apiError(err, 'Backup failed')); },
  });

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg">Backups</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            A consistent snapshot of the database (<code className="font-mono text-xs">VACUUM INTO</code>) on a schedule, kept in the data volume
            {status?.s3.configured ? ` and uploaded to ${status.s3.bucket}` : ''}. Restore by stopping the server and replacing the database file — see the README.
          </p>
        </div>
        {hasPermission('config.edit') && (
          <Button size="sm" onClick={() => run.mutate()} loading={run.isPending}>Back up now</Button>
        )}
      </div>

      {isLoading || !status ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          {!status.enabled && <Alert tone="info">Scheduled backups are off (<code className="font-mono text-xs">BACKUP_ENABLED=false</code>). Manual backups still work.</Alert>}
          {status.last_error && <Alert tone="error">Last backup failed: {status.last_error}</Alert>}
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Schedule</dt><dd className="font-mono mt-0.5">{status.schedule}</dd></div>
            <div><dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Keep</dt><dd className="mt-0.5">{status.keep === 0 ? 'all' : `${status.keep} newest`}</dd></div>
            <div><dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Off-site</dt><dd className="mt-0.5">{status.s3.configured ? 'S3' : 'none'}</dd></div>
            <div><dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Last run</dt><dd className="mt-0.5">{status.last_run_at ? formatDateTime(status.last_run_at) : 'not yet'}</dd></div>
          </dl>
          {status.files.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No snapshots in <code className="font-mono text-xs">{status.dir}</code> yet.</p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              {status.files.slice(0, 7).map(f => (
                <li key={f.name} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="font-mono text-xs truncate">{f.name}</span>
                  <span className="text-gray-500 dark:text-gray-400 tabular-nums shrink-0">{bytes(f.size_bytes)} · {formatDateTime(f.created_at)}</span>
                </li>
              ))}
              {status.files.length > 7 && <li className="py-1.5 text-xs text-gray-500">and {status.files.length - 7} more in {status.dir}</li>}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

export function RetentionCard() {
  const { data: policy } = useQuery({ queryKey: ['retention'], queryFn: api.getRetentionPolicy });
  return (
    <Card className="p-6 space-y-3">
      <h2 className="font-semibold text-lg">Retention</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Pruned nightly. Set with the <code className="font-mono text-xs">RETENTION_*_DAYS</code> variables; 0 keeps a table forever.
      </p>
      {policy && (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          {([
            ['Raw checks', policy.checks_days],
            ['Resolved incidents', policy.alerts_days],
            ['Hourly aggregates', policy.hourly_days],
            ['Notification deliveries', policy.deliveries_days],
            ['Audit log', policy.audit_days],
            ['API key access log', policy.audit_access_days],
          ] as [string, number][]).map(([label, n]) => (
            <div key={label}>
              <dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</dt>
              <dd className="mt-0.5">{days(n)}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}

export function ConfigFileCard() {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const qc = useQueryClient();
  const { data: state } = useQuery({ queryKey: ['config-file'], queryFn: api.getConfigFileState });
  const [plan, setPlan] = useState<ImportResult | null>(null);

  const planNow = useMutation({
    mutationFn: api.planConfigFile,
    onSuccess: setPlan,
    onError: err => addToast('error', apiError(err, 'Could not plan the config file')),
  });
  const applyNow = useMutation({
    mutationFn: api.applyConfigFile,
    onSuccess: (r) => {
      setPlan(null);
      qc.invalidateQueries({ queryKey: ['config-file'] });
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      addToast('success', `Applied: ${r.services.created + r.groups.created + r.notifications.created} created, ${r.services.updated + r.groups.updated + r.notifications.updated} updated`);
    },
    onError: err => { qc.invalidateQueries({ queryKey: ['config-file'] }); addToast('error', apiError(err, 'Could not apply the config file')); },
  });

  if (!state) return null;

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg">Config as code</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {state.path
              ? <>Groups, services and channels are managed from <code className="font-mono text-xs break-all">{state.path}</code>, applied on start and whenever the file changes. Entries it names are overwritten on the next apply; anything it does not mention is left alone.</>
              : <>Not in use. Set <code className="font-mono text-xs">PULSECHECK_CONFIG</code> to a YAML file (same shape as an export, plus a <code className="font-mono text-xs">slug</code> per entry) to manage this instance from a file. See the README.</>}
          </p>
        </div>
        {state.path && (
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="secondary" onClick={() => planNow.mutate()} loading={planNow.isPending}>Plan</Button>
            {hasPermission('config.edit') && <Button size="sm" onClick={() => applyNow.mutate()} loading={applyNow.isPending}>Apply now</Button>}
          </div>
        )}
      </div>

      {state.path && (
        <>
          {!state.exists && <Alert tone="error">The file does not exist yet. It will be applied when it appears.</Alert>}
          {state.last_error && (
            <Alert tone="error">
              Last apply failed: {state.last_error}
              {state.missing_env.length > 0 && <> — set {state.missing_env.map(v => <code key={v} className="font-mono text-xs ml-1">{v}</code>)}</>}
            </Alert>
          )}
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div><dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Watching</dt><dd className="mt-0.5">{state.watching ? 'yes' : 'no'}</dd></div>
            <div><dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Last applied</dt><dd className="mt-0.5">{state.last_loaded_at ? formatDateTime(state.last_loaded_at) : 'never'}</dd></div>
            {state.last_result && (
              <div><dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Last result</dt>
                <dd className="mt-0.5 tabular-nums">{state.last_result.services.created + state.last_result.groups.created + state.last_result.notifications.created} new · {state.last_result.services.updated + state.last_result.groups.updated + state.last_result.notifications.updated} updated</dd></div>
            )}
          </dl>
          {plan && (
            <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium">Plan (nothing applied)</p>
              <ImportPreview result={plan} />
              <div className="flex justify-end">
                <Button variant="secondary" size="sm" onClick={() => setPlan(null)}>Close</Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export function ApiDocsCard() {
  return (
    <Card className="p-6 space-y-2">
      <h2 className="font-semibold text-lg">API and integrations</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Every route is documented at{' '}
        <a href="/api/docs" target="_blank" rel="noreferrer" className="underline hover:no-underline">/api/docs</a>
        {' '}(<a href="/api/docs.json" target="_blank" rel="noreferrer" className="underline hover:no-underline">OpenAPI JSON</a>).
        Create an API key on your profile page; scrape Prometheus metrics from{' '}
        <code className="font-mono text-xs">/metrics</code> with the key or from an allow-listed network.
      </p>
    </Card>
  );
}
